import React, { useState } from 'react';

const keyframes = `
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(30px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @media (max-width: 900px) {
    .reels-header { padding: 0 30px 40px !important; }
    .reels-grid { padding: 0 30px !important; grid-template-columns: 1fr !important; gap: 40px !important; }
  }
  @media (max-width: 560px) {
    .reels-header { padding: 0 20px 32px !important; }
    .reels-grid { padding: 0 20px !important; }
  }
`;

// Add a reel by appending an entry: id, title, and a local thumbnail.
// Thumbnails live in /public/images/cinematics/thumb-<id>.jpg
const videos = [
  {
    id: 'YU5oVbJQ320',
    title: 'Serious / Regret / Sorrow',
    label: 'Acting Clip',
    thumb: '/images/cinematics/thumb-YU5oVbJQ320.jpg',
  },
  {
    id: 'VqIVd84p0vo',
    title: 'Angry / Disappointed / Hurt',
    label: 'Acting Clip',
    thumb: '/images/cinematics/thumb-VqIVd84p0vo.jpg',
  },
  {
    id: 'EGAYvAZtwRE',
    title: 'Funny / Angry / Over-Reaction',
    label: 'Acting Clip',
    thumb: '/images/cinematics/thumb-EGAYvAZtwRE.jpg',
  },
  {
    id: 'LsWLeEiHZmc',
    title: 'Humorous / Satire / Passive-Aggressive',
    label: 'Acting Clip',
    thumb: '/images/cinematics/thumb-LsWLeEiHZmc.jpg',
  },
  {
    id: 'bXqZkqGA1gY',
    title: 'Over-the-Top / Angry / Freakout',
    label: 'Acting Clip',
    thumb: '/images/cinematics/thumb-bXqZkqGA1gY.jpg',
  },
  {
    id: '5s_24DeVkLI',
    title: 'Sincere / Warm / Understanding',
    label: 'Acting Clip',
    thumb: '/images/cinematics/thumb-5s_24DeVkLI.jpg',
  },
  {
    id: '-CuprYZnAZU',
    title: 'Emotional / Introspective / Somber',
    label: 'Acting Clip',
    thumb: '/images/cinematics/thumb--CuprYZnAZU.jpg',
  },
  {
    id: '0qRyLqBiBrM',
    title: 'Emotional / Angry / Outburst',
    label: 'Acting Clip',
    thumb: '/images/cinematics/thumb-0qRyLqBiBrM.jpg',
  },
];

const styles = {
  page: {
    minHeight: '100vh',
    background: '#0a0a0a',
    paddingTop: '120px',
    paddingBottom: '100px',
  },
  header: {
    padding: '0 60px 60px',
    maxWidth: '1400px',
    margin: '0 auto',
  },
  label: {
    fontFamily: "'Outfit', sans-serif",
    fontSize: '0.7rem',
    letterSpacing: '0.35em',
    textTransform: 'uppercase',
    color: '#c9a96e',
    marginBottom: '16px',
    display: 'block',
  },
  heading: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: 'clamp(3rem, 6vw, 6rem)',
    fontWeight: 300,
    lineHeight: 1,
    color: '#f5f2ee',
  },
  grid: {
    padding: '0 60px',
    maxWidth: '1400px',
    margin: '0 auto',
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '40px',
  },
  card: {
    opacity: 0,
    animation: 'fadeUp 0.8s ease 0.2s forwards',
  },
  frame: {
    position: 'relative',
    aspectRatio: '16/9',
    background: '#0f0d0a',
    border: '1px solid rgba(201,169,110,0.15)',
    overflow: 'hidden',
  },
  thumb: {
    display: 'block',
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  playBtn: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '76px',
    height: '76px',
    borderRadius: '50%',
    border: '1px solid rgba(201,169,110,0.6)',
    background: 'rgba(10,10,10,0.55)',
    backdropFilter: 'blur(4px)',
    color: '#c9a96e',
    fontSize: '1.4rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingLeft: '5px',
    cursor: 'pointer',
    transition: 'all 0.25s ease',
  },
  meta: {
    marginTop: '18px',
  },
  metaLabel: {
    fontFamily: "'Outfit', sans-serif",
    fontSize: '0.65rem',
    letterSpacing: '0.25em',
    textTransform: 'uppercase',
    color: '#6b6560',
    display: 'block',
    marginBottom: '6px',
  },
  metaTitle: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: '1.5rem',
    fontWeight: 300,
    color: '#f5f2ee',
  },
  watchLink: {
    fontFamily: "'Outfit', sans-serif",
    fontSize: '0.65rem',
    letterSpacing: '0.2em',
    textTransform: 'uppercase',
    color: '#6b6560',
    textDecoration: 'none',
  },
};

function VideoCard({ video, index = 0 }) {
  // Click-to-play facade: YouTube's iframe (and its ~1MB of player JS) only
  // loads after the viewer asks for it, so the page stays fast.
  const [playing, setPlaying] = useState(false);

  return (
    <article style={{ ...styles.card, animationDelay: `${0.2 + index * 0.15}s` }}>
      <div style={styles.frame}>
        {playing ? (
          <iframe
            src={`https://www.youtube.com/embed/${video.id}?autoplay=1&rel=0&modestbranding=1`}
            title={video.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
          />
        ) : (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            aria-label={`Play ${video.title}`}
            style={{ ...styles.playBtn, border: '1px solid rgba(201,169,110,0.6)' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(201,169,110,0.22)';
              e.currentTarget.style.borderColor = '#c9a96e';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(10,10,10,0.55)';
              e.currentTarget.style.borderColor = 'rgba(201,169,110,0.6)';
            }}
          >
            <span style={{ transform: 'translateY(1px)' }}>&#9654;</span>
          </button>
        )}

        {!playing && (
          <img
            src={video.thumb}
            alt=""
            style={styles.thumb}
            loading="lazy"
            aria-hidden="true"
          />
        )}
      </div>

      <div style={styles.meta}>
        <span style={styles.metaLabel}>{video.label}</span>
        <span style={styles.metaTitle}>{video.title}</span>
        <br />
        <a
          href={`https://www.youtube.com/watch?v=${video.id}`}
          target="_blank"
          rel="noreferrer"
          style={{ ...styles.watchLink, color: '#6b6560' }}
          onMouseEnter={(e) => (e.currentTarget.style.color = '#c9a96e')}
          onMouseLeave={(e) => (e.currentTarget.style.color = '#6b6560')}
        >
          Watch on YouTube
        </a>
      </div>
    </article>
  );
}

export default function DemoReels() {
  return (
    <>
      <style>{keyframes}</style>
      <div style={styles.page}>
        <div className="reels-header" style={styles.header}>
          <span style={styles.label}>Showreel</span>
          <h1 style={styles.heading}>Demo Reels</h1>
        </div>

        <div className="reels-grid" style={styles.grid}>
          {videos.map((video, i) => (
            <VideoCard key={video.id} video={video} index={i} />
          ))}
        </div>
      </div>
    </>
  );
}
